import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Star, Globe, Clock, X, ChevronDown, ChevronUp } from "lucide-react";
import { Check, PlayCircle } from "lucide-react";

import {
    FaVideo,
    FaFileDownload,
    FaMobileAlt,
    FaInfinity,
    FaCertificate
} from 'react-icons/fa';
import { getCourseBySlug, validateCoupon } from "../api/api";
import { useNavigate } from 'react-router-dom';
import { FaMedal, FaStar, FaUsers, FaBook, FaWhatsapp, FaEnvelope } from 'react-icons/fa';
import AccessPopup from "./AccessPopup";
import CoursePurchasePopup from "./CoursePurchasePopup";

const CoursesDetailsHome = () => {
    const navigate = useNavigate();
    const { slug } = useParams();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeModule, setActiveModule] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showAccessModal, setShowAccessModal] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [showVideo, setShowVideo] = useState(false);
    const videoRef = useRef(null);
    
    // Coupon states
    const [showCouponInput, setShowCouponInput] = useState(false);
    const [couponCode, setCouponCode] = useState('');
    const [couponApplied, setCouponApplied] = useState(false);
    const [couponError, setCouponError] = useState('');
    const [couponMessage, setCouponMessage] = useState('');
    const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
    const [couponData, setCouponData] = useState(null);
    
    // Purchase modal state with key for forcing remount
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [purchaseModalKey, setPurchaseModalKey] = useState(0);
    
    const handleAddToCart = () => {
        navigate('/cart');
    };

    // Format price in Indian Rupees (same as FeaturedCourses)
    const formatIndianPrice = (amount) => {
        if (!amount) return '';
        const numAmount = parseFloat(amount);
        if (isNaN(numAmount) || numAmount <= 0) return '';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
        }).format(numAmount);
    };

    // Get effective price - prioritize early_bird_price, then discounted_price (if lower), then price
    const getEffectivePrice = () => {
        if (!course) return 0;
        const price = parseFloat(course.price) || 0;
        const discountedPrice = parseFloat(course.discounted_price) || 0;
        const earlyBirdPrice = parseFloat(course.early_bird_price) || 0;
        
        // Priority: early_bird_price (always use if available)
        if (earlyBirdPrice > 0) {
            return earlyBirdPrice;
        }
        // Check discounted price only if it's less than original price
        if (discountedPrice > 0 && discountedPrice < price) {
            return discountedPrice;
        }
        return price;
    };

    // Get the original price
    const getOriginalPrice = () => {
        if (!course) return 0;
        return parseFloat(course.price) || 0;
    };

    // Calculate discounted price with coupon
    const getFinalPrice = () => {
        if (!course) return null;
        let basePrice = getEffectivePrice();
        
        if (couponApplied && couponData) {
            if (couponData.final_amount !== undefined && couponData.final_amount !== null) {
                return couponData.final_amount;
            } else if (couponData.discount_type === 'percentage') {
                const discountAmount = (basePrice * (couponData.discount_value / 100));
                return basePrice - discountAmount;
            } else if (couponData.discount_type === 'fixed') {
                return basePrice - couponData.discount_value;
            }
        }
        return basePrice;
    };

    const getFinalPriceFormatted = () => {
        const finalPrice = getFinalPrice();
        if (finalPrice === null) return '';
        return formatIndianPrice(finalPrice);
    };

    // Handle coupon application with API
    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) {
            setCouponError('Please enter a coupon code');
            return;
        }

        setIsApplyingCoupon(true);
        setCouponError('');
        setCouponMessage('');

        try {
            const requestBody = {
                code: couponCode.trim().toUpperCase(),
                amount: getEffectivePrice(),
                type: "course",
                item_id: course.id
            };

            console.log("Coupon Request Body:", requestBody);

            const response = await validateCoupon(requestBody);
            
            console.log("Coupon Validation Response:", response);

            if (response.success === true && response.valid === true) {
                const couponInfo = response.coupon;
                setCouponApplied(true);
                setCouponData(couponInfo);
                setCouponError('');
                setCouponMessage(response.message || 'Coupon applied successfully!');
                setShowCouponInput(false);
                
                console.log(`Coupon applied: ${couponInfo.discount_value}% discount`);
            } else {
                setCouponError(response.message || 'Invalid coupon code');
                setCouponApplied(false);
                setCouponData(null);
            }
        } catch (error) {
            console.error("Coupon validation error:", error);
            
            if (error.response?.status === 404) {
                setCouponError('Coupon code not found');
            } else if (error.response?.status === 400) {
                setCouponError(error.response?.data?.message || 'Invalid coupon code');
            } else if (error.response?.status === 410) {
                setCouponError('This coupon has expired');
            } else {
                setCouponError(error.message || 'Failed to apply coupon. Please try again.');
            }
            
            setCouponApplied(false);
            setCouponData(null);
        } finally {
            setIsApplyingCoupon(false);
        }
    };

    // Remove coupon
    const handleRemoveCoupon = () => {
        setCouponApplied(false);
        setCouponCode('');
        setCouponMessage('');
        setCouponError('');
        setCouponData(null);
    };

    // Handle Buy Now button click
    const handleBuyNowClick = () => {
        console.log('Buy Now clicked, current modal state:', showPurchaseModal);
        
        // Close modal if already open
        if (showPurchaseModal) {
            setShowPurchaseModal(false);
            // Small delay to ensure state updates before reopening
            setTimeout(() => {
                setPurchaseModalKey(prev => prev + 1);
                setShowPurchaseModal(true);
            }, 100);
        } else {
            // Open modal
            setPurchaseModalKey(prev => prev + 1);
            setShowPurchaseModal(true);
        }
    };

    // Handle modal close
    const handleClosePurchaseModal = () => {
        console.log('Closing purchase modal');
        setShowPurchaseModal(false);
        // Reset any internal states if needed
        // Optionally reset coupon on close (uncomment if needed)
        // handleRemoveCoupon();
    };

    useEffect(() => {
        const handleContextMenu = (e) => {
            e.preventDefault();
        };

        const handleKeyDown = (e) => {
            if (
                (e.ctrlKey && (e.key === 's' || e.key === 'S' || e.key === 'u' || e.key === 'U')) ||
                (e.ctrlKey && e.shiftKey && e.key === 'i') ||
                e.key === 'F12'
            ) {
                e.preventDefault();
                return false;
            }
        };

        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    useEffect(() => {
        const fetchCourse = async () => {
            try {
                setLoading(true);
                const response = await getCourseBySlug(slug);
                console.log("Course Details Response:", response);

                if (response && response.success === true) {
                    setCourse(response.course);
                    setError(null);
                    if (response.course.modules && response.course.modules.length > 0) {
                        setActiveModule(response.course.modules[0].id);
                    }
                    setHasAccess(false);
                } else {
                    setCourse(null);
                    setError(response?.message || "Course not found");
                }
            } catch (error) {
                console.error("Error fetching course:", error);
                setCourse(null);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchCourse();
        }
    }, [slug]);

    const formatDuration = (minutes) => {
        if (!minutes || minutes <= 0) return null;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0 && mins > 0) {
            return `${hours}h ${mins}m`;
        } else if (hours > 0) {
            return `${hours}h`;
        } else if (mins > 0) {
            return `${mins}m`;
        }
        return null;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
            </div>
        );
    }

    if (error || !course) {
        return (
            <div className="text-center py-20">
                <p className="text-red-500">{error || "Course not found"}</p>
            </div>
        );
    }

    // Get prices for display (same logic as FeaturedCourses)
    const originalPrice = getOriginalPrice();
    const effectivePrice = getEffectivePrice();
    const hasDiscount = effectivePrice < originalPrice;
    const discountPercentage = hasDiscount ? Math.round(((originalPrice - effectivePrice) / originalPrice) * 100) : 0;

    // Check if early bird price is available
    const earlyBirdPrice = parseFloat(course.early_bird_price) || 0;
    const hasEarlyBird = earlyBirdPrice > 0 && earlyBirdPrice < originalPrice;

    // Check if course is coming soon
    const isComingSoon = course.is_coming_soon_status === true;

    return (
        <>
            <div className="bg-[#1f2a37] text-white py-16 px-6">
                <div className="max-w-6xl mx-auto">
                    <p className="text-sm text-gray-400 mb-4 font-lato">
                        <span
                            className="text-green-400 font-lato cursor-pointer hover:underline"
                            onClick={() => navigate('/')}
                        >
                            Home
                        </span>
                        &gt;{" "}
                        <span
                            className="text-green-400 font-lato cursor-pointer hover:underline"
                            onClick={() => navigate('/all-courses')}
                        >
                            Courses
                        </span>
                        &gt;{" "}
                        <span className="text-white font-lato">
                            {course.title}
                        </span>
                    </p>

                    <h1 className="text-3xl md:text-5xl leading-tight mb-4 font-merriweather">
                        {course.title}
                    </h1>

                    <p className="text-gray-300 max-w-2xl mb-6 font-lato">
                        {course.short_description}
                    </p>

                    <div className="flex flex-wrap items-center gap-4 mb-6 text-sm font-lato">
                        {course.is_bestseller && (
                            <span className="bg-gray-700 px-3 py-1 rounded-md flex items-center gap-1">
                                <FaMedal className="text-white" />
                                Bestseller
                            </span>
                        )}
                        <div className="flex items-center gap-1 text-yellow-400 font-lato">
                            <span className="text-white font-medium mr-1">{course.rating_display || course.rating}</span>
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} size={14} fill={i < Math.floor(course.rating) ? "currentColor" : "none"} />
                            ))}
                        </div>

                        <span className="text-gray-400">({course.total_ratings || 0} ratings)</span>
                        <span className="text-gray-400">{course.students_enrolled || 0} students enrolled</span>
                    </div>

                    <div className="flex items-center gap-3 mb-4">
                        <img
                            src={course.instructor?.profile_image}
                            alt="author"
                            className="w-10 h-10 rounded-full object-cover"
                        />
                        <p className="text-sm text-gray-300 font-lato">
                            Created by{" "}
                            <span className="text-white font-medium font-lato">
                                {course.instructor_name}
                            </span>
                        </p>
                    </div>

                    <div className="flex items-center gap-6 text-sm text-gray-400 font-lato">
                        <div className="flex items-center gap-1 font-lato">
                            <Clock size={14} />
                            Last updated {new Date(course.last_updated).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-1 font-lato">
                            <Globe size={14} />
                            {course.language}
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white py-16 px-6">
                <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-10">
                    <div className="md:col-span-2">
                        {course.what_you_learn && course.what_you_learn.length > 0 && (
                            <div className="bg-[#f8f9fa] p-8 rounded-xl shadow-sm border mb-10">
                                <h2 className="text-xl font-semibold text-gray-800 mb-6 font-merriweather">
                                    What you'll learn
                                </h2>
                                <div className="grid md:grid-cols-2 gap-4 font-lato">
                                    {course.what_you_learn.map((item, index) => (
                                        <div key={index} className="flex items-start gap-2">
                                            <Check
                                                className="text-green-500 w-5 h-5 mt-1 flex-shrink-0"
                                            />
                                            <p className="text-sm text-gray-600">{item.replace(/\r/g, '')}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="bg-white p-8 rounded-xl shadow-sm border">
                            <h2 className="text-xl font-semibold text-gray-800 mb-6 font-merriweather">
                                Course Description
                            </h2>
                            <div className="border-t mb-6"></div>
                            <div className="prose prose-sm max-w-none font-lato">
                                {course.long_description?.split('\n').map((paragraph, index) => {
                                    if (!paragraph.trim()) return null;

                                    const isHeading = paragraph.trim().startsWith('#') ||
                                        (paragraph.trim().length < 50 &&
                                            paragraph.trim().toUpperCase() === paragraph.trim() &&
                                            !paragraph.trim().endsWith('.'));

                                    const isBulletPoint = paragraph.trim().startsWith('•') ||
                                        paragraph.trim().startsWith('-') ||
                                        paragraph.trim().startsWith('*');

                                    if (isHeading) {
                                        const headingText = paragraph.replace(/^#+/g, '').trim();
                                        return (
                                            <h3 key={index} className="text-lg font-semibold text-gray-800 mt-6 mb-3 font-merriweather">
                                                {headingText}
                                            </h3>
                                        );
                                    }

                                    if (isBulletPoint) {
                                        const bulletText = paragraph.replace(/^[•\-*]\s*/, '').trim();
                                        return (
                                            <div key={index} className="flex items-start gap-2 mb-2">
                                                <span className="text-green-500 mt-1">•</span>
                                                <span className="text-gray-600 text-sm leading-relaxed">{bulletText}</span>
                                            </div>
                                        );
                                    }

                                    return (
                                        <p key={index} className="text-gray-600 text-sm leading-relaxed mb-4">
                                            {paragraph.trim()}
                                        </p>
                                    );
                                })}
                            </div>
                        </div>
                        {course.modules && course.modules.length > 0 && (
                            <div className="bg-white rounded-xl shadow-sm border mt-8">
                                <div className="flex justify-between items-center p-5">
                                    <h2 className="text-lg font-semibold text-gray-800 font-merriweather">
                                        Course Syllabus
                                    </h2>
                                    <span
                                        className="text-green-500 text-xs cursor-pointer hover:underline"
                                        onClick={() => {
                                            if (activeModule === "all") {
                                                setActiveModule(course.modules[0]?.id);
                                            } else {
                                                setActiveModule("all");
                                            }
                                        }}
                                    >
                                        {activeModule === "all" ? "Collapse all" : "Expand all"}
                                    </span>
                                </div>

                                <div className="border-t"></div>

                                <div className="px-5 py-2">
                                    <p className="text-xs text-gray-500">
                                        {course.modules.length} Modules • {course.statistics?.total_lectures || 0} Lessons
                                        {course.statistics?.total_duration && ` • ${course.statistics.total_duration}`}
                                    </p>
                                </div>

                                <div className="space-y-2 px-5 pb-5">
                                    {course.modules.map((module) => (
                                        <div key={module.id} className="border rounded-lg overflow-hidden">
                                            <div
                                                onClick={() => setActiveModule(activeModule === module.id ? null : module.id)}
                                                className="flex justify-between items-center bg-gray-50 px-4 py-3 cursor-pointer hover:bg-gray-100 transition"
                                            >
                                                <p className="text-sm font-medium text-gray-800">
                                                    {module.title}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {module.lectures_count} lessons
                                                    {module.duration_minutes > 0 && ` • ${formatDuration(module.duration_minutes)}`}
                                                </p>
                                            </div>

                                            {(activeModule === module.id || activeModule === "all") && module.data_videos_attachment && module.data_videos_attachment.length > 0 && (
                                                <div className="bg-white px-4 py-3 space-y-3">
                                                  {module.data_videos_attachment.map((item) => {
    const titleText = item.title || "";
    const trimmedTitle = titleText.trim();
    const description = item.description || "";

    // Check if title has multiple items (for splitting)
    const hasMultipleItems =
        trimmedTitle.includes('•') ||
        trimmedTitle.includes('*') ||
        trimmedTitle.includes('-') ||
        trimmedTitle.includes(',') ||
        trimmedTitle.includes('\n') ||
        trimmedTitle.match(/\d+\./);

    if (hasMultipleItems) {
        let items = [];
        if (trimmedTitle.match(/\d+\./)) {
            const parts = trimmedTitle.split(/(\d+\.\s*)/).filter(p => p.trim());
            let currentItem = '';
            for (let i = 0; i < parts.length; i++) {
                if (parts[i].match(/^\d+\.\s*$/)) {
                    if (currentItem) {
                        items.push(currentItem.trim());
                        currentItem = '';
                    }
                    currentItem = parts[i];
                } else if (currentItem) {
                    currentItem += ' ' + parts[i].trim();
                } else {
                    const clean = parts[i].trim();
                    if (clean) items.push(clean);
                }
            }
            if (currentItem) {
                items.push(currentItem.trim());
            }
        } else if (trimmedTitle.includes('•') || trimmedTitle.includes('*') || trimmedTitle.includes('-')) {
            items = trimmedTitle.split(/[•\-*]\s*/).filter(p => p.trim());
        } else if (trimmedTitle.includes('\n')) {
            items = trimmedTitle.split('\n').filter(p => p.trim());
        } else if (trimmedTitle.includes(',')) {
            items = trimmedTitle.split(',').filter(p => p.trim());
        } else {
            items = [trimmedTitle];
        }

        const isNumbered = items.some(item => item.match(/^\d+\./));
        const cleanItems = items.map(item => {
            return item.replace(/^\d+\.\s*/, '').trim();
        }).filter(p => p);

        return (
            <div key={item.id} className="border-b border-gray-100 last:border-0 py-2">
                <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs">{item.type === "video" ? "▶" : "📄"}</span>
                    <span className="text-xs text-gray-500 font-medium">
                        {item.type === "video" ? "Video" : "Resource"}
                    </span>
                    <span className="text-xs text-gray-400 ml-auto">
                        {item.duration_formatted || item.file_size_formatted || ""}
                    </span>
                </div>
                {isNumbered ? (
                    <ul className="space-y-1 mt-1 ml-4 list-decimal list-inside">
                        {cleanItems.map((point, idx) => {
                            if (!point) return null;
                            return (
                                <li key={idx} className="text-sm text-gray-700">
                                    {point}
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <ul className="space-y-1 mt-1 ml-4">
                        {cleanItems.map((point, idx) => {
                            if (!point) return null;
                            return (
                                <li key={idx} className="flex items-start gap-1.5">
                                    <span className="text-green-500 text-xs mt-0.5 flex-shrink-0">•</span>
                                    <span className="text-sm text-gray-700">{point}</span>
                                </li>
                            );
                        })}
                    </ul>
                )}
                {/* Description - rendered as-is with HTML */}
                {description && (
                    <div 
                        className="text-xs text-gray-500 mt-1 description-content"
                        dangerouslySetInnerHTML={{ __html: description }}
                    />
                )}
            </div>
        );
    }

    // Single item
    return (
        <div key={item.id} className="border-b border-gray-100 last:border-0 py-2">
            <div className="flex items-center gap-2">
                <span className="text-gray-400 text-xs">{item.type === "video" ? "▶" : "📄"}</span>
                <span className="text-sm text-gray-700">{trimmedTitle}</span>
                <span className="text-xs text-gray-400 ml-auto">
                    {item.duration_formatted || item.file_size_formatted || ""}
                </span>
            </div>
            {/* Description - rendered as-is with HTML */}
            {description && (
                <div 
                    className="text-xs text-gray-500 mt-1 description-content"
                    dangerouslySetInnerHTML={{ __html: description }}
                />
            )}
        </div>
    );
})}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {course.instructor && (
                            <div className="bg-white p-8 rounded-xl shadow-sm border mt-10">
                                <h2 className="text-xl font-semibold text-gray-800 mb-6 font-merriweather">
                                    Meet Your Instructor
                                </h2>
                                <div className="border-t mb-6"></div>

                                <div className="flex flex-col md:flex-row gap-6">
                                    <div className="flex-shrink-0">
                                        <img
                                            src={course.instructor.profile_image}
                                            alt={course.instructor.name}
                                            className="w-24 h-24 rounded-full object-cover"
                                        />
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800 font-merriweather">
                                            {course.instructor.name} {course.instructor.credentials && `(${course.instructor.credentials})`}
                                        </h3>
                                        <p className="text-green-600 text-sm font-medium mt-1 font-lato">
                                            {course.instructor.title}
                                        </p>

                                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mt-2 font-lato">
                                            <span className="flex items-center gap-1 text-yellow-500">
                                                <FaStar className="text-yellow-500" /> {course.instructor.rating} Instructor Rating
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <FaUsers /> {course.instructor.total_students} Students
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <FaBook /> {course.instructor.total_courses} Courses
                                            </span>
                                        </div>

                                        <p className="text-gray-600 text-sm leading-relaxed mt-4 max-w-2xl font-lato">
                                            {course.instructor.bio}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="relative md:-mt-40 z-10 md:sticky md:top-6 self-start">
                        <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-6">
                            <div className="relative mb-5 cursor-pointer group" onClick={() => course.intro_video_url && setShowVideo(true)}>
                                <img
                                    src={course.image_url_full}
                                    alt={course.title}
                                    className="rounded-lg w-full h-44 object-cover"
                                />
                                {course.intro_video_url && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-all">
                                        <div className="bg-white p-3 rounded-full shadow-md hover:scale-110 transition-transform">
                                            <PlayCircle className="text-green-500 w-10 h-10" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Price Display - Same logic as FeaturedCourses */}
                            <div className="flex items-center gap-3 mb-4 font-lato">
                                {isComingSoon ? (
                                    <p className="text-2xl font-bold text-gray-600">
                                        Coming Soon
                                    </p>
                                ) : couponApplied && couponData ? (
                                    <>
                                        <p className="text-2xl font-bold text-green-600">
                                            {getFinalPriceFormatted()}
                                        </p>
                                        <p className="text-sm text-gray-400 line-through">
                                            {formatIndianPrice(originalPrice)}
                                        </p>
                                        <p className="text-sm text-red-500 font-medium ml-auto">
                                            {couponData.discount_type === 'percentage' 
                                                ? `${couponData.discount_value}% Off`
                                                : `₹${couponData.discount_value} Off`}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        {/* Show early bird price if available and valid */}
                                        {hasEarlyBird ? (
                                            <>
                                                <p className="text-2xl font-bold text-green-600">
                                                    {formatIndianPrice(earlyBirdPrice)}
                                                </p>
                                                <p className="text-sm text-gray-400 line-through">
                                                    {formatIndianPrice(originalPrice)}
                                                </p>
                                                {discountPercentage > 0 && (
                                                    <p className="text-sm text-red-500 font-medium ml-auto">
                                                        {discountPercentage}% Off
                                                    </p>
                                                )}
                                            </>
                                        ) : hasDiscount ? (
                                            <>
                                                <p className="text-2xl font-bold text-green-600">
                                                    {formatIndianPrice(effectivePrice)}
                                                </p>
                                                <p className="text-sm text-gray-400 line-through">
                                                    {formatIndianPrice(originalPrice)}
                                                </p>
                                                {discountPercentage > 0 && (
                                                    <p className="text-sm text-red-500 font-medium ml-auto">
                                                        {discountPercentage}% Off
                                                    </p>
                                                )}
                                            </>
                                        ) : (
                                            /* Show Original Price */
                                            <p className="text-2xl font-bold text-gray-900">
                                                {formatIndianPrice(originalPrice)}
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Button with access condition */}
                            {!isComingSoon ? (
                                <button
                                    onClick={handleBuyNowClick}
                                    className="w-full bg-[#6dbe45] text-white py-2.5 rounded-lg font-lato mb-3 hover:bg-green-600 transition font-medium"
                                >
                                    Buy Now
                                </button>
                            ) : (
                                <button
                                    disabled
                                    className="w-full bg-gray-300 text-gray-500 py-2.5 rounded-lg font-lato mb-3 cursor-not-allowed font-medium"
                                >
                                    Coming Soon
                                </button>
                            )}

                            {/* Coupon Code Section - Only show if NOT coming soon */}
                            {!isComingSoon && (
                                <div className="mt-3 border-t pt-3">
                                    {couponApplied && couponData ? (
                                        <div className="flex items-center justify-between bg-green-50 p-2 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <span className="text-green-600 text-sm font-medium">✓ Coupon Applied</span>
                                                <span className="text-xs text-gray-500">({couponCode})</span>
                                                <span className="text-xs text-green-600 font-medium">
                                                    {couponData.discount_type === 'percentage' 
                                                        ? `${couponData.discount_value}% off` 
                                                        : `₹${couponData.discount_value} off`}
                                                </span>
                                            </div>
                                            <button
                                                onClick={handleRemoveCoupon}
                                                className="text-xs text-red-500 hover:text-red-700 font-medium"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => setShowCouponInput(!showCouponInput)}
                                                className="text-sm text-gray-600 hover:text-green-600 font-medium flex items-center justify-center gap-1 transition w-full"
                                            >
                                                Have a Coupon Code?
                                                {showCouponInput ? (
                                                    <ChevronUp size={16} />
                                                ) : (
                                                    <ChevronDown size={16} />
                                                )}
                                            </button>

                                            {showCouponInput && (
                                                <div className="mt-2 flex flex-col gap-2">
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="text"
                                                            value={couponCode}
                                                            onChange={(e) => {
                                                                setCouponCode(e.target.value);
                                                                setCouponError('');
                                                            }}
                                                            placeholder="Enter coupon code"
                                                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                                            disabled={isApplyingCoupon}
                                                        />
                                                        <button
                                                            onClick={handleApplyCoupon}
                                                            disabled={isApplyingCoupon}
                                                            className="bg-[#6dbe45] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                                        >
                                                            {isApplyingCoupon ? 'Applying...' : 'Apply'}
                                                        </button>
                                                    </div>
                                                    {couponError && (
                                                        <p className="text-xs text-red-500">{couponError}</p>
                                                    )}
                                                    {couponMessage && (
                                                        <p className="text-xs text-green-600">{couponMessage}</p>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {course.includes && course.includes.length > 0 && (
                                <div className="mt-5 border-t pt-4">
                                    <p className="text-sm font-medium text-gray-700 mb-2 font-lato">
                                        This course includes:
                                    </p>
                                    <ul className="space-y-2 text-sm text-gray-600 font-lato">
                                        {course.includes.map((item, index) => {
                                            const IconComponent = item.icon ? (
                                                <img
                                                    src={item.icon}
                                                    alt={item.title}
                                                    className="w-4 h-4"
                                                />
                                            ) : (
                                                (() => {
                                                    const icons = [
                                                        <FaVideo className="w-4 h-4 text-gray-500" />,
                                                        <FaFileDownload className="w-4 h-4 text-gray-500" />,
                                                        <FaMobileAlt className="w-4 h-4 text-gray-500" />,
                                                        <FaInfinity className="w-4 h-4 text-gray-500" />,
                                                        <FaCertificate className="w-4 h-4 text-gray-500" />
                                                    ];
                                                    return icons[index % icons.length];
                                                })()
                                            );

                                            return (
                                                <li key={index} className="flex items-center gap-2">
                                                    <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                                                        {IconComponent}
                                                    </span>
                                                    <span className="text-gray-700">
                                                        {item.title ? item.title.replace(/\r/g, '') : ''}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                            <div className="flex justify-between mt-5 text-xs text-gray-500 border-t pt-3">
                                <span
                                    onClick={() => setShowShareModal(true)}
                                    className="cursor-pointer hover:underline font-lato"
                                >
                                    Share
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showShareModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
                    <div className="bg-white rounded-xl w-[90%] max-w-md p-6 relative">
                        <button
                            onClick={() => setShowShareModal(false)}
                            className="absolute right-5 top-4 text-gray-600"
                        >
                            <X size={22} />
                        </button>

                        <h2 className="text-lg font-semibold mb-5">
                            Share this course
                        </h2>

                        <div className="flex gap-2">
                            <input
                                value={window.location.href}
                                readOnly
                                className="border rounded-md px-3 py-2 w-full text-sm"
                            />
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(window.location.href)
                                }}
                                className="bg-[#5bb532] text-white px-5 rounded-md"
                            >
                                Copy
                            </button>
                        </div>

                        <div className="flex justify-center gap-6 mt-5">
                            <button
                                onClick={() => {
                                    window.open(
                                        `https://wa.me/?text=${window.location.href}`,
                                        "_blank"
                                    )
                                }}
                                className="border border-[#5bb532] text-[#5bb532] rounded-full w-10 h-10 flex items-center justify-center hover:bg-[#5bb532] hover:text-white transition"
                            >
                                <FaWhatsapp size={18} />
                            </button>

                            <button
                                onClick={() => {
                                    window.open(
                                        `mailto:?subject=Course&body=${window.location.href}`
                                    )
                                }}
                                className="border border-[#5bb532] text-[#5bb532] rounded-full w-10 h-10 flex items-center justify-center hover:bg-[#5bb532] hover:text-white transition"
                            >
                                <FaEnvelope size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showAccessModal && (
                <AccessPopup
                    open={showAccessModal}
                    onClose={() => setShowAccessModal(false)}
                    course={course}
                />
            )}
            {showVideo && (
                <div
                    className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
                    onClick={() => setShowVideo(false)}
                >
                    <div
                        className="bg-white rounded-xl w-full max-w-4xl relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setShowVideo(false)}
                            className="absolute -top-12 right-0 text-white hover:text-gray-300 transition"
                        >
                            <X size={28} />
                        </button>
                        <div className="p-4">
                            <video
                                ref={videoRef}
                                controls
                                autoPlay
                                className="w-full rounded-lg"
                                style={{ maxHeight: '80vh' }}
                                poster={course.image_url_full}
                                controlsList="nodownload noremoteplayback noplaybackrate"
                                disablePictureInPicture
                                onContextMenu={(e) => e.preventDefault()}
                                onError={(e) => {
                                    console.error("Video error:", e);
                                    alert("Video cannot be played. Please check your connection or try again later.");
                                }}
                            >
                                <source src={course.intro_video_url} type="application/x-mpegURL" />
                                <p>Your browser does not support HLS videos. Please use a modern browser.</p>
                            </video>
                        </div>
                    </div>
                </div>
            )}
            {showPurchaseModal && (
                <CoursePurchasePopup
                    key={purchaseModalKey}
                    open={showPurchaseModal}
                    onClose={handleClosePurchaseModal}
                    course={course}
                    couponCode={couponApplied ? couponCode : null}
                    couponData={couponApplied ? couponData : null}
                    finalPrice={getFinalPrice()}
                />
            )}
        </>
    );
};

export default CoursesDetailsHome;